import { useQuery } from "@tanstack/react-query";
import { get } from "./api";
import { useSession } from "./session";
import type { AlertSummary } from "./types";

/**
 * Open alerts by severity — the one figure two places need.
 *
 * The rail's badge and the Alerts page's masthead ask the same question, and one
 * hook means one query key: React Query dedupes the request across both, and a
 * resolve on the page updates the badge because they share a cache entry rather
 * than because somebody remembered to invalidate two.
 *
 * Its own endpoint rather than a count over the list, for the reason the refund
 * queue summary already documents: an answer computed from the visible rows is a
 * fact about pagination.
 */
export const ALERT_SUMMARY_KEY = ["alerts", "summary"] as const;

/**
 * Polled, because nothing raises alerts on the operator's schedule. A minute is
 * chosen against the thing being watched: the sweeper's own tick is thirty
 * seconds, so a badge that lags by up to a minute is never more than one tick
 * behind the condition it describes, and a panel left open all day costs 480
 * requests rather than a request per second.
 */
const REFRESH_MS = 60_000;

export function useAlertSummary() {
  const { can } = useSession();

  return useQuery({
    queryKey: ALERT_SUMMARY_KEY,
    /**
     * Not asked at all without `alerts.view`. The rail renders on every page, so
     * an operator without it would otherwise take a 403 a minute, all day.
     */
    enabled: can("alerts.view"),
    queryFn: () => get<AlertSummary>("/alerts/summary"),
    refetchInterval: REFRESH_MS,
    /**
     * A failing summary must not put an error on every page in the panel — the
     * badge is ambient, and its absence reads correctly as "no number to show".
     * The Alerts page surfaces real failures through its own list query.
     */
    retry: false,
  });
}

/** Everything open, at any severity. The badge's number. */
export function openTotal(summary: AlertSummary | undefined): number {
  if (summary === undefined) return 0;
  return summary.open.critical + summary.open.error + summary.open.warning;
}
